import { useState } from 'react'
import './ContactForm.css'

const ContactForm = ({ onSubmit, onCancel, initialData = null }) => {
  const [formData, setFormData] = useState({
    name: initialData?.name || '',
    email: initialData?.email || '',
    phone: initialData?.phone || '',
    company: initialData?.company || '',
    tags: initialData?.tags?.join(', ') || ''
  })
  const [loading, setLoading] = useState(false)

  const cleanContactName = (name) => {
    if (!name) return ''
    
    // Remove "1st degree connection" (case-insensitive, with variations)
    let cleaned = name
      .replace(/\s*1st\s+degree\s+connection\s*/gi, ' ')
      .replace(/\s*1st\s+degree\s*/gi, ' ')
      .replace(/\s*first\s+degree\s+connection\s*/gi, ' ')
      .replace(/\s*first\s+degree\s*/gi, ' ')
    
    // Trim whitespace from beginning and end
    cleaned = cleaned.trim()
    
    // Remove extra spaces (replace multiple spaces with single space)
    cleaned = cleaned.replace(/\s+/g, ' ')
    
    return cleaned
  }

  const extractCompanyFromEmail = (email) => {
    if (!email) return ''
    
    // Extract domain from email
    const emailRegex = /^[^\s@]+@([^\s@]+)$/
    const match = email.match(emailRegex)
    if (!match) return ''
    
    const domain = match[1].toLowerCase()
    
    // Don't extract if domain looks incomplete (less than 3 characters or no valid TLD pattern)
    // This prevents extraction on partial domains like "acme.c" or "ac"
    const domainParts = domain.split('.')
    const lastPart = domainParts[domainParts.length - 1]
    const commonTLDs = ['com', 'org', 'net', 'edu', 'gov', 'co', 'io', 'ai', 'app', 'dev', 'tech', 'online', 'site', 'website', 'store', 'shop', 'info', 'biz', 'us', 'uk', 'au', 'ca', 'de', 'fr', 'jp', 'cn', 'in', 'br', 'mx', 'es', 'it', 'nl', 'se', 'no', 'dk', 'fi', 'pl', 'cz', 'at', 'ch', 'be', 'ie', 'nz', 'za', 'ae', 'sa', 'il', 'tr', 'ru', 'kr', 'sg', 'hk', 'tw', 'my', 'th', 'ph', 'id', 'vn']
    
    // Check if TLD looks complete (at least 2 characters and matches common TLDs or looks valid)
    const hasCompleteTLD = lastPart.length >= 2 && (
      commonTLDs.includes(lastPart) || 
      /^[a-z]{2,}$/.test(lastPart)
    )
    
    // If domain doesn't have a complete TLD, don't extract yet
    if (!hasCompleteTLD || domainParts.length < 2) {
      return ''
    }
    
    // List of free email providers to exclude
    const freeEmailProviders = [
      'gmail.com',
      'yahoo.com',
      'yahoo.co.uk',
      'hotmail.com',
      'outlook.com',
      'live.com',
      'msn.com',
      'aol.com',
      'icloud.com',
      'me.com',
      'mac.com',
      'protonmail.com',
      'proton.me',
      'zoho.com',
      'mail.com',
      'gmx.com',
      'yandex.com',
      'inbox.com',
      'fastmail.com',
      'hey.com',
      'tutanota.com',
      'mail.ru',
      'qq.com',
      '163.com',
      'sina.com',
      'rediffmail.com',
      'hotmail.co.uk',
      'outlook.co.uk',
      'live.co.uk'
    ]
    
    // Check if it's a free email provider
    if (freeEmailProviders.includes(domain)) {
      return ''
    }
    
    // Extract company name from domain
    // Remove common TLDs and subdomains
    let companyName = domain
    
    // Remove www. prefix if present
    companyName = companyName.replace(/^www\./, '')
    
    // Remove common TLDs (.com, .org, .net, etc.)
    companyName = companyName.replace(/\.(com|org|net|edu|gov|co|io|ai|app|dev|tech|online|site|website|store|shop|info|biz|us|uk|au|ca|de|fr|jp|cn|in|br|mx|es|it|nl|se|no|dk|fi|pl|cz|at|ch|be|ie|nz|za|ae|sa|il|tr|ru|kr|sg|hk|tw|my|th|ph|id|vn|jp|co\.uk|co\.nz|co\.za|co\.jp|com\.au|com\.br|com\.mx|com\.sg|com\.hk|com\.tw|com\.my|com\.th|com\.ph|com\.id|com\.vn)$/i, '')
    
    // If nothing left after removing TLD, use the original domain without TLD
    if (!companyName || companyName === domain) {
      // Fallback: just remove the last part after the last dot
      const parts = domain.split('.')
      if (parts.length > 1) {
        parts.pop() // Remove TLD
        companyName = parts.join('.')
      } else {
        companyName = domain
      }
    }
    
    // Capitalize first letter of each word
    companyName = companyName
      .split(/[.-]/)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ')
    
    return companyName
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)

    try {
      const tagsArray = formData.tags
        ? formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag)
        : []

      // Clean the contact name before submitting
      const cleanedName = cleanContactName(formData.name)

      await onSubmit({
        ...formData,
        name: cleanedName,
        tags: tagsArray
      })
    } catch (error) {
      console.error('Error submitting form:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleChange = (e) => {
    let value = e.target.value
    
    // Clean name field in real-time as user types/pastes
    if (e.target.name === 'name') {
      // Remove "1st degree connection" patterns as user types/pastes
      // This happens immediately so user sees the cleaned version
      value = value
        .replace(/\s*1st\s+degree\s+connection\s*/gi, ' ')
        .replace(/\s*1st\s+degree\s*/gi, ' ')
        .replace(/\s*first\s+degree\s+connection\s*/gi, ' ')
        .replace(/\s*first\s+degree\s*/gi, ' ')
      
      // Don't trim while typing (user might be adding spaces)
      // Full cleanup happens on blur and submit
    }
    
    const updatedFormData = {
      ...formData,
      [e.target.name]: value
    }
    
    // Auto-extract company from email when email is entered
    if (e.target.name === 'email' && !initialData) {
      // Only extract if email looks complete (has @ and at least one dot in domain)
      // This prevents extraction on incomplete emails like "john@ac"
      const emailParts = value.split('@')
      const hasCompleteDomain = emailParts.length === 2 && emailParts[1]?.includes('.')
      
      if (hasCompleteDomain) {
        // Extract company from the current email value
        const extractedCompany = extractCompanyFromEmail(value)
        
        // Check if company was previously auto-populated by comparing with previous email extraction
        const previousExtracted = extractCompanyFromEmail(formData.email)
        const wasAutoPopulated = formData.company === previousExtracted
        
        // Auto-populate if:
        // 1. Company field is empty, OR
        // 2. Company was previously auto-populated (so we can update it as email changes)
        if ((!formData.company || formData.company.trim() === '') || wasAutoPopulated) {
          if (extractedCompany) {
            updatedFormData.company = extractedCompany
          } else if (wasAutoPopulated) {
            // Clear company if email changed to a free email provider
            updatedFormData.company = ''
          }
        }
      }
    }
    
    setFormData(updatedFormData)
  }

  const handleNameBlur = (e) => {
    // Clean up name when field loses focus
    const cleaned = cleanContactName(e.target.value)
    setFormData({
      ...formData,
      name: cleaned
    })
  }

  const handleEmailBlur = (e) => {
    // Extract company from email when email field loses focus
    // Only for new contacts and if company field is empty
    if (!initialData && (!formData.company || formData.company.trim() === '')) {
      const extractedCompany = extractCompanyFromEmail(e.target.value)
      if (extractedCompany) {
        setFormData({
          ...formData,
          company: extractedCompany
        })
      }
    }
  }

  return (
    <div className="form-overlay">
      <div className="form-modal">
        <div className="form-header">
          <h3>{initialData ? 'Edit Contact' : 'New Contact'}</h3>
          <button className="close-btn" onClick={onCancel}>×</button>
        </div>
        
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="name">Name *</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              onBlur={handleNameBlur}
              required
              placeholder="John Doe"
            />
          </div>

          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              onBlur={handleEmailBlur}
              placeholder="john@example.com"
            />
          </div>

          <div className="form-group">
            <label htmlFor="phone">Phone</label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder="+1 (555) 123-4567"
            />
          </div>

          <div className="form-group">
            <label htmlFor="company">Company</label>
            <input
              type="text"
              id="company"
              name="company"
              value={formData.company}
              onChange={handleChange}
              placeholder="Acme Inc."
            />
          </div>

          <div className="form-group">
            <label htmlFor="tags">Tags (comma-separated)</label>
            <input
              type="text"
              id="tags"
              name="tags"
              value={formData.tags}
              onChange={handleChange}
              placeholder="lead, customer, vip"
            />
          </div>

          <div className="form-actions">
            <button type="button" className="btn-secondary" onClick={onCancel}>
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={loading}>
              {loading ? 'Saving...' : initialData ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default ContactForm

